import { Button, Drawer, Form, Input, Switch, Radio, Transfer, message, Skeleton } from 'antd';
import type { TransferDirection } from 'antd/es/transfer';
import { useForm } from 'antd/es/form/Form';
import { Rule } from 'antd/lib/form';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';
import areaCode from '@/assets/json/areaCode.json';
import ProvinceCityCascader from '../../ProvinceCityCascader';
import { useSelector } from 'react-redux';
import { getAddressPins, getPostalCodeAddress } from '@/api/newApi/personalCenter/address';
import { countryCodeEnum } from '@/util/constance';
import PhoneField from '../PhoneField';
import { LoadingOutlined } from '@ant-design/icons';
import {
  findMatchingPostalCodeAddress,
  isPostalCodeAutofillCountry,
  PostalCodeAddress,
} from '@/util/postalCodeAddress';
const { Item, useWatch } = Form;

interface Props {
  address: any;
  visible: boolean;
  onOk: (address: any) => void;
  onCancel: () => void;
  loading?: boolean;
}
const AddArea = ({ visible, onOk, onCancel, loading = false, ...rest }: Props) => {
  // 表单
  const [form] = useForm();
  // 中英文翻译
  // 获取用户信息
  const currentUser: any = useSelector((state: any) => {
    return state?.User?.data?.[0] ?? {};
  });
  // 用户类型：4=管理员, 5=采购员
  const userType = currentUser?.userType;
  // 是否显示可见范围选择（只有管理员才显示，采购员新增地址时默认仅自己可见且不显示）
  const showVisibilityRange = userType === 4;

  // Stabilize address object reference to prevent unnecessary re-renders in ProvinceCityCascader
  // This helps fix the mobile Safari bug where cascader values get cleared
  // 使用对象序列化来检测内容变化，确保编辑后能正确更新
  const addressDataKey = JSON.stringify({
    id: rest.address?.addressManagerId || rest.address?.addressId || rest.address?.contractId,
    receiverName: rest.address?.receiverName || rest.address?.receiveName,
    receiverPhone: rest.address?.receiverPhone || rest.address?.receivePhone,
    modified: rest.address?.modified || rest.address?.created,
    countryId: rest.address?.countryId,
    provinceId: rest.address?.provinceId,
    cityId: rest.address?.cityId,
    countyId: rest.address?.countyId,
    countryName: rest.address?.countryName,
    provinceName: rest.address?.provinceName,
    cityName: rest.address?.cityName,
    countyName: rest.address?.countyName,
    detailAddress: rest.address?.detailAddress,
    postalCode: rest.address?.postalCode,
    authScope: rest.address?.authScope,
    selectedPins: rest.address?.selectedPins,
    defaultStatus: rest.address?.defaultStatus,
  });
  const addressData = useMemo(() => rest.address, [addressDataKey]);
  // 是否显示默认地址选项（只有采购员才显示）
  const showDefaultAddress = userType === 5;

  // 是否修改过表单
  const [isFormModified, setIsFormModified] = useState(false);

  // 判断是否是新增地址
  const isNewAddress = useMemo(() => {
    return !addressData || (!addressData.addressManagerId && !addressData.addressId && !addressData.contractId && !addressData.id);
  }, [addressData]);

  const allocType = useWatch('allocType', form) || 1;
  const defaultStatus = useWatch('defaultStatus', form);
  const isPartialVisible = allocType === 3;

  // 穿梭框相关状态
  const [transferDataSource, setTransferDataSource] = useState<any[]>([]);
  const [transferTargetKeys, setTransferTargetKeys] = useState<string[]>([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [postalCodeAddress, setPostalCodeAddress] = useState<PostalCodeAddress | null>(null);
  const [postalCodeLookupLoading, setPostalCodeLookupLoading] = useState(false);
  const postalCodeRequestRef = useRef(0);
  const postalCodeLookupPromiseRef = useRef<Promise<void> | null>(null);
  const postalCodeLookupRequiredRef = useRef(false);

  // 对区号进行去重（按value去重，保留第一个）
  const uniqueAreaCode = useMemo(() => {
    const seen = new Set<string>();
    return areaCode.filter((item: any) => {
      if (seen.has(item.value)) {
        return false;
      }
      seen.add(item.value);
      return true;
    });
  }, []);

  // 创建国家名称到区号的映射
  const countryNameToAreaCode = useMemo(() => {
    const map: Record<string, string> = {};
    areaCode.forEach((item: any) => {
      if (!map[item.name]) {
        map[item.name] = item.value;
      }
    });
    return map;
  }, []);

  /**
   * 保存逻辑
   */
  const onSave = async () => {
    // 直接从邮编输入框点击保存时，先等待失焦触发的查询完成，避免提交回填前的旧地址。
    if (postalCodeLookupPromiseRef.current) {
      await postalCodeLookupPromiseRef.current;
    } else if (postalCodeLookupRequiredRef.current) {
      await handlePostalCodeLookup();
    }

    form.validateFields().then(values => {
      // 如果是部分可见但没有选择账号，提示用户
      if (values.allocType === 3 && (!transferTargetKeys || transferTargetKeys.length === 0)) {
        message.warning(TLT(`请选择部分可见的账号`));
        return;
      }

      console.log('values', values);
      values.receivePhone = `${values.areaCode} ${values.receivePhone}`;
      values.userPins = transferTargetKeys; // 传递选中的账号列表

      // 确保 defaultStatus 正确传递（Switch 的 checked 值）
      // 由于 defaultStatus 在 footer 中使用 noStyle，validateFields 可能不会包含它
      // 所以需要单独获取，优先使用 validateFields 返回的值，如果没有则从表单获取
      console.log('onSave - values from validateFields:', values);
      console.log('onSave - values.defaultStatus:', values.defaultStatus);
      const defaultStatusValue = values.defaultStatus !== undefined
        ? values.defaultStatus
        : form.getFieldValue('defaultStatus');
      console.log('onSave - defaultStatusValue from form.getFieldValue:', defaultStatusValue, 'type:', typeof defaultStatusValue);
      // 确保是布尔值：true/false 或 1/0 都转换为布尔值
      // 如果值为 undefined 或 null，默认为 false
      values.defaultStatus = defaultStatusValue === true || defaultStatusValue === 1;
      console.log('onSave - final defaultStatus:', values.defaultStatus, 'type:', typeof values.defaultStatus);

      onOk({ ...addressData, ...values });
    });
  };

  // 穿梭框数据变化处理
  const handleTransferChange = (targetKeys: React.Key[], direction: TransferDirection, moveKeys: React.Key[]) => {
    const keys = targetKeys.map(key => String(key));
    setTransferTargetKeys(keys);
    form.setFieldsValue({ userPins: keys });
    setIsFormModified(true);
  };

  async function handlePostalCodeLookup() {
    if (postalCodeLookupPromiseRef.current) return postalCodeLookupPromiseRef.current;
    if (!visible) return;
    const postalCode = String(form.getFieldValue('postalCode') || '').trim();
    if (!postalCode) {
      postalCodeLookupRequiredRef.current = false;
      return;
    }

    // 仅指定欧洲国家的登录用户启用邮编回填；查询结果仍按页面当前收货国家匹配。
    if (!isPostalCodeAutofillCountry(currentUser?.countryCode)) {
      postalCodeLookupRequiredRef.current = false;
      return;
    }

    const requestId = ++postalCodeRequestRef.current;
    postalCodeLookupRequiredRef.current = false;
    const lookupPromise = (async () => {
      setPostalCodeLookupLoading(true);
      try {
        const res: any = await getPostalCodeAddress(postalCode);
        if (requestId !== postalCodeRequestRef.current) return;
        if (res?.success === false) {
          message.error(res.msg || TLT('邮政编码查询失败，请重试'));
          return;
        }

        const selectedCountryId = form.getFieldValue('countryId') || form.getFieldValue('countryArea');
        const selectedCountryCode = form.getFieldValue('countryCode') || form.getFieldValue('country');
        // 后端排序继续服务其它场景；表单回填必须从全部结果中匹配页面当前一级地址。
        const nextAddress = findMatchingPostalCodeAddress(
          res,
          selectedCountryId,
          selectedCountryCode,
          postalCode,
        );
        if (!nextAddress) {
          // 无当前国家匹配结果时保持客户已选地址，由客户继续手动补充。
          return;
        }
        // 一级地址由客户当前选择决定；先写下三级，保证查询完成后立即保存也提交回填后的地址。
        form.setFieldsValue({
          provinceId: nextAddress.provinceId || null,
          cityId: nextAddress.cityId || null,
          countyId: nextAddress.countyId || null,
        });
        setPostalCodeAddress(nextAddress);
      } catch (error) {
        if (requestId !== postalCodeRequestRef.current) return;
        console.error('postal code address autofill failed', error);
        message.error(TLT('邮政编码查询失败，请重试'));
      } finally {
        if (requestId === postalCodeRequestRef.current) {
          setPostalCodeLookupLoading(false);
        }
      }
    })();

    postalCodeLookupPromiseRef.current = lookupPromise;
    try {
      await lookupPromise;
    } finally {
      if (postalCodeLookupPromiseRef.current === lookupPromise) {
        postalCodeLookupPromiseRef.current = null;
      }
    }
  }

  // 初始化地址弹窗逻辑
  useEffect(() => {
    if (visible && !loading) {
      postalCodeRequestRef.current += 1;
      postalCodeLookupPromiseRef.current = null;
      postalCodeLookupRequiredRef.current = false;
      setPostalCodeLookupLoading(false);
      setPostalCodeAddress(null);
      console.log('useEffect - visible:', visible, 'loading:', loading, 'addressData:', addressData);
      form.resetFields();
      setIsFormModified(false);

      // 判断是新增还是编辑
      // const isNewAddress = !addressData || !addressData.addressManagerId && !addressData.addressId && !addressData.contractId && !addressData.id;

      if (isNewAddress) {
        // 新增模式：根据用户国家预选
        let defaultAreaCode = uniqueAreaCode.length > 0 ? uniqueAreaCode[0].value : '+86';

        // 获取用户国家信息（从jdig/account/me接口返回的数据中获取，和userType在同一个地方）
        const userCountryCode = currentUser?.countryCode;
        const userCountryName = userCountryCode ? (countryCodeEnum as Record<string, string>)[userCountryCode] : null;

        // 根据用户国家匹配区号
        if (userCountryName && countryNameToAreaCode[userCountryName]) {
          defaultAreaCode = countryNameToAreaCode[userCountryName];
        }

        // 设置表单初始值
        form.setFieldsValue({
          receiveName: '',
          areaCode: defaultAreaCode,
          receivePhone: '',
          defaultStatus: false,
          countryArea: '',
          countryId: null,
          provinceId: null,
          cityId: null,
          countyId: null,
          countryCode: userCountryCode || '',
          allocType: 1,
          userPins: [],
        });

        setTransferTargetKeys([]);

        // 如果有用户国家，需要异步加载国家列表后设置默认国家
        // 这个逻辑会在ProvinceCityCascader中处理
      } else {
        // 编辑模式：使用已有数据
        let phone = addressData.receiverPhone || addressData.receivePhone || '';
        let areaCode = '+86';
        if (phone.includes(' ')) {
          areaCode = phone.split(' ')[0];
          phone = phone.split(' ')[1];
        }

        console.log('编辑模式 - addressData.defaultStatus:', addressData.defaultStatus, 'type:', typeof addressData.defaultStatus);
        // 计算 defaultStatus 的值，确保正确转换为布尔值
        // 支持多种格式：1, '1', true, 'true'
        const defaultStatusValue = addressData.defaultStatus === 1
          || addressData.defaultStatus === '1'
          || addressData.defaultStatus === true
          || addressData.defaultStatus === 'true'
          || Number(addressData.defaultStatus) === 1;
        console.log('编辑模式 - defaultStatusValue:', defaultStatusValue);

        // 一次性设置所有字段，包括 defaultStatus，确保值正确
        form.setFieldsValue({
          receiveName: addressData.receiverName || addressData.receiveName || '', // 兼容字段名
          areaCode,
          receivePhone: phone,
          countryArea: addressData?.countryId || '',
          countryId: addressData?.countryId || null, // 设置 countryId 供 ProvinceCityCascader 使用
          provinceId: addressData?.provinceId || null, // 设置 provinceId 供 ProvinceCityCascader 使用
          cityId: addressData?.cityId || null, // 设置 cityId 供 ProvinceCityCascader 使用
          countyId: addressData?.countyId || null, // 设置 countyId 供 ProvinceCityCascader 使用
          countryCode: addressData?.countryCode || addressData?.country || '', // 国家二字码
          detailAddress: addressData?.detailAddress || '', // 详细地址
          postalCode: addressData?.postalCode || '', // 邮政编码
          allocType: addressData?.authScope || addressData?.allocType || 1, // 兼容 authScope 字段
          userPins: addressData?.userPins || addressData?.selectedPins || [], // 选中的账号列表
          defaultStatus: defaultStatusValue, // 直接在这里设置，确保值正确
        });

        // 验证表单值是否设置成功
        setTimeout(() => {
          const formDefaultStatus = form.getFieldValue('defaultStatus');
          console.log('编辑模式 - 设置后 form.getFieldValue("defaultStatus"):', formDefaultStatus, 'type:', typeof formDefaultStatus);
        }, 100);

        // 如果是编辑模式且是部分可见，设置已选账号
        // 兼容 authScope 和 allocType 字段
        const authScope = addressData?.authScope || addressData?.allocType || 1;
        const userPins = addressData?.userPins || addressData?.selectedPins || [];
        if (authScope === 3) {
          const selectedPins = Array.isArray(userPins)
            ? userPins.filter(p => p) // 过滤空值
            : (typeof userPins === 'string' ? userPins.split(',').filter(p => p.trim()) : []);
          setTransferTargetKeys(selectedPins);
          form.setFieldsValue({ userPins: selectedPins });
        } else {
          setTransferTargetKeys([]);
        }
      }
    }
  }, [visible, addressData, uniqueAreaCode, countryNameToAreaCode, currentUser, loading, form]);

  // 当 loading 变为 false 且是编辑模式时，确保 defaultStatus 正确设置
  // 这个 useEffect 专门处理 loading 状态变化后的值设置
  useEffect(() => {
    if (visible && !loading && addressData) {
      const isNewAddress = !addressData.addressManagerId && !addressData.addressId && !addressData.contractId && !addressData.id;
      if (!isNewAddress) {
        // 编辑模式：确保 defaultStatus 正确设置
        const defaultStatusValue = addressData.defaultStatus === 1
          || addressData.defaultStatus === '1'
          || addressData.defaultStatus === true
          || addressData.defaultStatus === 'true'
          || Number(addressData.defaultStatus) === 1;
        // 使用 setTimeout 确保表单已经渲染完成
        setTimeout(() => {
          const currentValue = form.getFieldValue('defaultStatus');
          // 如果当前值与期望值不一致，重新设置
          if (currentValue !== defaultStatusValue) {
            console.log('修复 defaultStatus - 当前值:', currentValue, '期望值:', defaultStatusValue);
            form.setFieldsValue({ defaultStatus: defaultStatusValue });
          }
        }, 50);
      }
    }
  }, [visible, loading, addressData, form]);

  // 加载 pin 列表
  const loadAddressPins = async () => {
    if (!isPartialVisible || !visible) return;

    setTransferLoading(true);
    try {
      const res: any = await getAddressPins();
      if (res.success) {
        // res.value 应该是 pin 数组，如 ["caochuan-V5", "caochuan-V4", "caochuan-V2"]
        const pins = res.value || [];
        // 转换为 Transfer 组件需要的格式：[{ key: string, title: string }]
        const dataSource = pins.map((pin: string) => ({
          key: pin,
          title: pin,
        }));
        setTransferDataSource(dataSource);
      } else {
        message.error(res.msg || TLT(`加载账号列表失败`));
        setTransferDataSource([]);
      }
    } catch (error) {
      console.error('加载账号列表失败:', error);
      message.error(TLT(`加载账号列表失败`));
      setTransferDataSource([]);
    } finally {
      setTransferLoading(false);
    }
  };

  // 当可见范围变为"部分可见"时，加载账号列表
  useEffect(() => {
    if (isPartialVisible && visible) {
      loadAddressPins();
    } else if (!isPartialVisible && visible) {
      // 只有在不是部分可见时才清空数据源
      setTransferDataSource([]);
      // 但是不清空 targetKeys，因为它可能在初始化时已经设置好了
      // 只有当用户主动切换可见范围时才清空
    }
  }, [isPartialVisible, visible]);
  // form校验规则
  const rules: { [k: string]: Rule[] } = {
    // 收货人
    receiveName: [{ required: true, message: TLT(`请输入收货人姓名`) }],
    // 电话，只校验必填，不校验格式（因为每个国家的电话号码格式不同）
    receivePhone: [
      {
        required: true,
        message: TLT(`请输入收货人电话号码`),
      },
    ],
    // 国家
    country: [{ required: true, message: TLT(`请输入国家的名字`) }],
    // 详细地址
    detailAddress: [{ required: true, message: TLT(`请输入详细地址`) }],
    postalCode: [{ required: true, whitespace: true, message: TLT(`请输入邮政编码`) }],
  };
  return (
    <Drawer
      width={800}
      title={`${(addressData?.contractId || addressData?.addressId || addressData?.addressManagerId || addressData?.id) ? TLT(`编辑`) : TLT(`新建`)}${TLT(`收货地址`)}`}
      open={visible}
      rootClassName={styles.drawer}
      onClose={onCancel}
      destroyOnClose={true}
      footer={
        <div className={styles.footer}>
          {showDefaultAddress && (
            <div className={styles.footerSwitchWrapper}>
              <Switch
                checked={!!defaultStatus}
                onChange={(checked) => {
                  form.setFieldsValue({ defaultStatus: checked });
                  setIsFormModified(true);
                  try {
                    log('jdisp', 'Settlement_default');
                  } catch (e) {
                    console.error(e);
                  }
                }}
                className={styles.switch}
              />
              <span className={styles.switchLabel}>{TLT(`设置为默认地址`)}</span>
            </div>
          )}
          <div className={styles.footerButtons}>
            <Button onClick={onCancel} className={styles.cancelBtn}>
              {TLT(`返回`)}
            </Button>
            <Button
              type="primary"
              onClick={onSave}
              className={styles.saveBtn}
              disabled={loading || (!isNewAddress && !isFormModified)}
            >
              {TLT(`保存`)}
            </Button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div style={{ padding: '0 24px' }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : (
        <Form form={form} layout="vertical" className={styles.form} onValuesChange={() => setIsFormModified(true)}>
          {/* 收货人 */}
          <Item label={TLT(`收货人`)} name="receiveName" rules={rules.receiveName} className={styles.formItem}>
            <Input placeholder={TLT(`请输入收货人姓名`)} maxLength={100} className={styles.input} showCount></Input>
          </Item>
          {/* 区号 + 电话 */}
          <PhoneField
            label={TLT(`电话号码`)}
            required
            formItemClassName={styles.formItem}
            selectClassName={styles.select}
            inputClassName={styles.input}
            areaCodeName="areaCode"
            phoneName="receivePhone"
            phoneRules={rules.receivePhone}
            phonePlaceholder={TLT(`请输入收货人电话号码`)}
            showCount
          />
          {/* 邮编先于级联地址输入，指定欧洲国家可用查询结果直接完成下方地址回填。 */}
          <Item label={TLT(`邮政编码`)} name="postalCode" rules={rules.postalCode} className={styles.formItem}>
            <Input
              placeholder={TLT(`请输入邮政编码`)}
              maxLength={20}
              className={`${styles.input} ${styles.postalCodeInput}`}
              suffix={postalCodeLookupLoading ? <LoadingOutlined spin /> : null}
              aria-busy={postalCodeLookupLoading}
              onChange={() => {
                // 输入变化后立即废弃旧响应，保证只允许最新邮编回填地址。
                postalCodeRequestRef.current += 1;
                postalCodeLookupPromiseRef.current = null;
                postalCodeLookupRequiredRef.current = true;
                setPostalCodeLookupLoading(false);
                setPostalCodeAddress(null);
              }}
              onBlur={() => {
                void handlePostalCodeLookup();
              }}
              onPressEnter={event => {
                event.preventDefault();
                event.currentTarget.blur();
              }}
            />
          </Item>
          {/* 地址选择 */}
          <div className={styles.addressSection}>
            <ProvinceCityCascader
              detailData={{ address: addressData }}
              formRef={form}
              postalCodeAddress={postalCodeAddress}
            />
          </div>
          {/* 详细地址 */}
          <Item label={TLT(`详细地址`)} name="detailAddress" rules={rules.detailAddress} className={styles.formItem}>
            <Input.TextArea
              placeholder={TLT(`请输入详细地址`)}
              maxLength={500}
              rows={4}
              className={styles.textArea}
              showCount
            ></Input.TextArea>
          </Item>
          {/* 隐藏字段：默认地址状态，确保 Footer 中的 Switch 能正常工作 */}
          <Item name="defaultStatus" hidden>
            <Input />
          </Item>
          {/* 隐藏字段：国家二字码，确保 ProvinceCityCascader 设置的值能被提交 */}
          <Item name="countryCode" hidden>
            <Input />
          </Item>
          {/* 隐藏字段：国家二字码 (兼容字段 country) */}
          <Item name="country" hidden>
            <Input />
          </Item>
          {/* 可见范围 */}
          {showVisibilityRange && (
            <>
              <Item label={TLT(`可见范围`)} name="allocType" className={styles.formItem}>
                <Radio.Group className={styles.radioGroup}>
                  <Radio value={1}>{TLT(`仅自己可见`)}</Radio>
                  <Radio value={2}>{TLT(`全部可见`)}</Radio>
                  <Radio value={3}>{TLT(`部分可见`)}</Radio>
                </Radio.Group>
              </Item>
              {/* 部分可见时的账号选择穿梭框 */}
              {isPartialVisible && (
                <div className={styles.accountSelectionSection}>
                  <div className={styles.accountSelectionTitle}>
                    {TLT(`请选择可见账号`)}
                  </div>
                  <Transfer
                    dataSource={transferDataSource}
                    titles={[TLT(`可选账号`), TLT(`已选账号`)]}
                    targetKeys={transferTargetKeys}
                    onChange={handleTransferChange}
                    render={(item) => item.title}
                    listStyle={{
                      width: 300,
                      height: 300,
                    }}
                    className={styles.transfer}
                  />
                </div>
              )}
            </>
          )}
        </Form>
      )}
    </Drawer>
  );
};

export default AddArea;
